import { AuthContainer } from "../container/AuthContainer.tsx";
import { useVerificationCode } from "../hooks/useVerificationCode.ts";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    SEARCH_PARAM_CODE,
    SEARCH_PARAM_INDEX
} from "../../../src/shared";
import { fetchWithError } from "../utils/fetchWithError.ts";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { CheckCircle, Error } from "@mui/icons-material";

enum VerificationStatus {
    PENDING,
    SUCCESS,
    ERROR
}

export const ProfileVerificationScreen = () => {
    const { index, code } = useVerificationCode();
    const [searchParams] = useSearchParams();
    // Get clientId directly from URL, not from localStorage
    // This ensures that for local registration (without clientId), no clientId is used
    const clientId = searchParams.get("clientId");
    const [verificationStatus, setVerificationStatus] =
        useState<VerificationStatus>(VerificationStatus.PENDING);
    const loginUrl = new URL("/app/login", window.location.href);

    // Only add clientId to login URL if it was in the verification URL
    if (clientId) {
        loginUrl.searchParams.append("clientId", clientId);
    }

    useEffect(() => {
        // Don't wait for clientId - it's optional for local registration
        const url = new URL("/auth/verify-user", window.location.href);
        url.searchParams.append(SEARCH_PARAM_CODE, code);
        url.searchParams.append(SEARCH_PARAM_INDEX, index);
        // Only add clientId if it exists
        if (clientId) {
            url.searchParams.append("clientId", clientId);
        }

        fetchWithError<{ success: boolean }>(url)
            .then((res) => {
                if (res.success) {
                    setVerificationStatus(VerificationStatus.SUCCESS);
                } else {
                    setVerificationStatus(VerificationStatus.ERROR);
                }
            })
            .catch((error) => {
                console.error("Verification error:", error);
                setVerificationStatus(VerificationStatus.ERROR);
            });
    }, [clientId, index, code]);

    return (
        <AuthContainer>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column"
                }}
            >
                {verificationStatus === VerificationStatus.PENDING && (
                    <>
                        <Typography>Verifiziere . . .</Typography>
                        <CircularProgress size="3rem" sx={{ my: 3 }} />
                    </>
                )}
                {verificationStatus === VerificationStatus.SUCCESS && (
                    <>
                        <CheckCircle
                            color={"success"}
                            fontSize={"large"}
                            sx={{ my: 3 }}
                        />
                        <Typography>
                            Dein Konto wurde erfolgreich aktiviert!
                        </Typography>
                        <Button href={loginUrl.href} sx={{ my: 3 }}>
                            Zum Login
                        </Button>
                    </>
                )}
                {verificationStatus === VerificationStatus.ERROR && (
                    <>
                        <Error
                            color={"error"}
                            fontSize={"large"}
                            sx={{ my: 3 }}
                        />
                        <Typography>
                            Bei der Aktivierung ist ein Fehler aufgetreten.
                        </Typography>
                        <Typography>
                            Bitte wende dich an support@tintoa.net
                        </Typography>
                    </>
                )}
            </Box>
        </AuthContainer>
    );
};
